import { useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../../../identity/types';
import { peopleGateway } from '../../../people';
import type { DepartmentOption } from '../../../people/types';
import { GuidedField } from '../../guidance/GuidedField';
import { fieldDef } from '../../guidance/roleFieldRegistry';
import { GuidedErrorCode } from '../../types';
import { ModalDialog } from '../ModalDialog';
import { departmentFailure } from './departmentFailure';

/**
 * "Add department" over the Job Role wizard: a nested, accessible dialog (focus trapped, Escape and Cancel close it without touching the wizard).
 *
 * It asks for the three things the server requires (name, code, description). The code is SUGGESTED from the name through the API and is
 * editable; once the person edits it, it is never replaced again. Saving calls the real API with `rejectDuplicates: true`, so a name or code that
 * already exists is refused with a message at that field instead of silently reusing the other department. A failure keeps the dialog and every
 * typed value, and shows a plain message with the operation (reference) id the server sent.
 *
 * Optional department details the department model does not have (parent, head, cost center, work location, effective date) are not offered.
 * The department is saved for the whole company as soon as it is saved here; the role itself is saved only by the wizard's own Save.
 */

export interface AddDepartmentDialogProps {
  /** A name to start from (for example a recommended standard name the person chose). */
  initialName?: string;
  onClose: () => void;
  /** The department the server created: the wizard selects it. */
  onCreated: (department: DepartmentOption) => void;
}

interface FieldErrors { name?: string | undefined; code?: string | undefined; description?: string | undefined }

const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{1,19}$/;
const clean = (value: string) => value.replace(/\s+/g, ' ').trim();

export function AddDepartmentDialog({ initialName = '', onClose, onCreated }: AddDepartmentDialogProps) {
  const uid = useId();
  const [name, setName] = useState(initialName);
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<{ message: string; reference: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const codeEdited = useRef(false);
  const [codeTyped, setCodeTyped] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);

  // A suggested code, from the name, as long as the person has not typed their own.
  useEffect(() => {
    const trimmed = clean(name);
    if (codeEdited.current || !trimmed) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSuggesting(true);
      peopleGateway
        .suggestDepartmentCode(trimmed, controller.signal)
        .then((s) => { if (!controller.signal.aborted && !codeEdited.current) setCode(s.suggestion); })
        .catch(() => { /* no suggestion: the person types the code */ })
        .finally(() => { if (!controller.signal.aborted) setSuggesting(false); });
    }, 350);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [name]);

  useEffect(() => {
    if (failure) failureRef.current?.focus();
  }, [failure]);

  // The fields are disabled while a save is in flight, and focus() on a disabled field does nothing. So after the server refuses a name or code, the focus move waits
  // for this effect, which runs only once `busy` is false and the fields are enabled. (A zero-delay timer used to do this and could fire before React had re-enabled them.)
  const focusAfterSave = useRef<FieldErrors | null>(null);
  useEffect(() => {
    if (busy || !focusAfterSave.current) return;
    focusFirst(focusAfterSave.current);
    focusAfterSave.current = null;
  }, [busy, errors]);

  function validate() {
    const found: FieldErrors = {};
    const n = clean(name);
    const c = code.trim().toUpperCase();
    const d = description.trim();
    if (!n) found.name = 'Enter a department name.';
    else if (n.length > 100) found.name = 'The department name must be 100 characters or fewer.';
    if (!c) found.code = 'Enter a department code. EPN suggests one when you have typed the name.';
    else if (!CODE_PATTERN.test(c)) found.code = 'Use 2 to 20 letters, numbers, dashes or underscores, starting with a letter or number.';
    if (!d) found.description = 'Enter a short description of the department.';
    else if (d.length > 500) found.description = 'The description must be 500 characters or fewer.';
    return found;
  }

  function focusFirst(found: FieldErrors) {
    if (found.name) nameRef.current?.focus();
    else if (found.code) codeRef.current?.focus();
    else if (found.description) descriptionRef.current?.focus();
  }

  async function submit() {
    if (busy) return;
    const found = validate();
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length > 0) {
      focusFirst(found);
      return;
    }
    setBusy(true);
    try {
      const created = await peopleGateway.createDepartmentStrict({ name: clean(name), code: code.trim().toUpperCase(), description: description.trim() });
      onCreated(created);
    } catch (err) {
      if (err instanceof ApiError && err.code === GuidedErrorCode.DepartmentNameExists) {
        const next = { name: err.message };
        setErrors(next);
        focusAfterSave.current = next;
      } else if (err instanceof ApiError && (err.code === GuidedErrorCode.DepartmentCodeExists || err.code === GuidedErrorCode.DepartmentCodeInvalid)) {
        const next = { code: err.message };
        setErrors(next);
        focusAfterSave.current = next;
      } else {
        setFailure(departmentFailure(err));
      }
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="Add a department"
      size="md"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy}>{busy ? 'Saving…' : 'Save department'}</button>
        </>
      )}
    >
      <p className="muted" id={`${uid}-intro`}>
        The department is saved for your whole company as soon as you save it here. The role you are writing is not saved until you save it yourself, and
        everything you have entered so far is kept whatever you choose here.
      </p>
      {failure && (
        <div ref={failureRef} tabIndex={-1} className="jr-error jr-error--block" role="alert" data-testid="department-failure">
          <p>{failure.message}</p>
          {failure.reference && <p>Reference: <strong>{failure.reference}</strong>. Quote it if you contact support.</p>}
        </div>
      )}
      <GuidedField field={fieldDef('departmentName')} error={errors.name} counter={{ length: name.length, max: 100 }}>
        {(a) => (
          <input
            {...a} ref={nameRef} data-autofocus type="text" autoComplete="off" maxLength={120} value={name} disabled={busy}
            onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
          />
        )}
      </GuidedField>
      <GuidedField
        field={fieldDef('departmentCode')} error={errors.code}
        hint={suggesting ? 'Suggesting a code…' : codeTyped ? 'Saved in upper case.' : 'Suggested from the name. You can change it.'}
      >
        {(a) => (
          <input
            {...a} ref={codeRef} type="text" autoComplete="off" autoCapitalize="characters" maxLength={20} value={code} disabled={busy} className="jr-upper"
            onChange={(e) => { codeEdited.current = true; setCodeTyped(true); setCode(e.target.value.toUpperCase()); setErrors((p) => ({ ...p, code: undefined })); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
          />
        )}
      </GuidedField>
      <GuidedField field={fieldDef('departmentDescription')} error={errors.description} counter={{ length: description.length, max: 500 }}>
        {(a) => (
          <textarea
            {...a} ref={descriptionRef} rows={3} value={description} disabled={busy}
            onChange={(e) => { setDescription(e.target.value); setErrors((p) => ({ ...p, description: undefined })); }}
          />
        )}
      </GuidedField>
    </ModalDialog>
  );
}
